import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    DeductionDailyBreakdown,
    DeductionDetailRequest,
    DeductionDetailResponse,
    DeductionItem,
} from '../types/deduction';
import { getDeductionDetail } from '../api/deductionApi';

export const useDeductionDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [item, setItem] = useState<DeductionItem | null>(null);
    const [dailyBreakdownOverride, setDailyBreakdownOverride] = useState<DeductionDailyBreakdown[] | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const detailParams = useMemo<DeductionDetailRequest | null>(() => {
        const search = new URLSearchParams(location.search);
        const state = location.state as { item?: DeductionItem } | null;
        const seqNoFromState = state?.item?.seqNo ?? null;
        const seqNoFromSearch = search.get('seqNo');
        const seqNo =
            seqNoFromSearch !== null
                ? Number(seqNoFromSearch)
                : seqNoFromState;

        if (seqNo === null || Number.isNaN(seqNo)) {
            return null;
        }

        return {
            seqNo,
        };
    }, [location.search, location.state]);

    useEffect(() => {
        if (!detailParams) {
            setError('상세 조회에 필요한 정보가 없습니다.');
            setItem(null);
            setDailyBreakdownOverride(null);
            return;
        }

        let isMounted = true;

        const fetchDetail = async () => {
            setIsLoading(true);
            setError(null);
            setDailyBreakdownOverride(null);

            try {
                const response = await getDeductionDetail(detailParams);

                if (!isMounted) return;

                if (response.success) {
                    const detail = extractDetailItem(response);

                    if (detail) {
                        setItem(detail);
                    } else {
                        setItem(null);
                    }

                    const override = extractDailyBreakdown(response, detail ?? undefined);
                    if (override) {
                        setDailyBreakdownOverride(override);
                    }
                } else {
                    setError(response.message || '상세 정보를 불러오지 못했습니다.');
                    setItem(null);
                }
            } catch (fetchError) {
                if (!isMounted) return;
                setError('상세 정보를 불러오는 중 오류가 발생했습니다.');
                setItem(null);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchDetail();

        return () => {
            isMounted = false;
        };
    }, [detailParams]);

    const parseDate = useCallback((value: string) => {
        const parts = value.split('-');
        if (parts.length !== 3) return null;
        const year = Number(parts[0]);
        const month = Number(parts[1]) - 1;
        const day = Number(parts[2]);
        const parsed = new Date(year, month, day);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }, []);

    const formatDateLabel = useCallback((date: Date) => {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${year}-${month}-${day}`;
    }, []);

    const getStatusLabel = useCallback((terminatedYn: string) => {
        return terminatedYn === 'N' ? '진행 중' : '종료';
    }, []);

    const getCalcTypeLabel = useCallback((calcType: string) => {
        if (calcType === '1' || calcType?.toUpperCase() === 'FIXED') {
            return '정액 (공제액)';
        }
        if (calcType === '2' || calcType?.toUpperCase() === 'RATE') {
            return '정률 (공제율)';
        }
        return calcType || '-';
    }, []);

    const formatAmount = useCallback((amount: number | null) => {
        if (amount === null) return '-';
        return `${amount.toLocaleString()} 원`;
    }, []);

    const dailyBreakdown = useMemo<DeductionDailyBreakdown[]>(() => {
        if (dailyBreakdownOverride) {
            return dailyBreakdownOverride;
        }
        return [];
    }, [dailyBreakdownOverride]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        item,
        dailyBreakdown,
        getStatusLabel,
        getCalcTypeLabel,
        formatAmount,
        handleBack,
        isLoading,
        error,
    };
};

const extractDetailItem = (response: DeductionDetailResponse): DeductionItem | null => {
    return response.deductInfo ?? response.deductDetail ?? response.deduction ?? null;
};

const extractDailyBreakdown = (
    response: DeductionDetailResponse,
    detail?: DeductionItem
): DeductionDailyBreakdown[] | null => {
    if (Array.isArray(response.dailyList) && response.dailyList.length > 0) {
        return response.dailyList.map((item) => {
            const label = item.deductDay ?? (item as { tranDay?: string | null }).tranDay ?? '';
            return {
                label: label || '-',
                amount: item.deductAmt ?? null,
                rate: item.deductRate ?? null,
            };
        });
    }

    if (detail?.tranDay) {
        return [
            {
                label: detail.tranDay,
                amount: detail.deductAmt ?? null,
                rate: detail.deductRate ?? null,
            },
        ];
    }

    return null;
};

